import type {
    CreateContrastCheck,
    CreateChartCheckKey,
    CreateThemeTokens,
    CreateChartCheck,
    CreateThemeKey,
    CreatePalette
} from "./settings";
import {
    CHART_LIGHTNESS_BANDS,
    CHART_CHROMA_FLOOR,
    CHART_CONTRAST_MIN,
    CHART_NORMAL_FLOOR,
    CHART_SERIES_COUNT,
    type ChartPairDelta,
    CHART_SERIES_PATH,
    getChartPairDelta,
    CHART_CVD_TARGET
} from "./chart";
import {
    getContrastRatio,
    type ColorVector,
    getApcaContrast,
    compositeOver,
    rgbToOklch,
    parseColor,
    formatHex
} from "./color";

type ContrastPair = {
    isApcaInfoInDark?: boolean;
    wcagTarget: number;
    apcaTarget: number;
    background: string;
    foreground: string;
    labelKey: string;
    id: string;
};

type TokenTree = {
    [key: string]: TokenTree | string;
};

type ChartPair = {
    delta: ChartPairDelta;
    second: number;
    first: number;
};

const GROUND_PATH = "content.container.default";

const TEXT_WCAG = 4.5;

const MARK_WCAG = 3;

const BODY_APCA = 75;

const TEXT_APCA = 60;

const ICON_APCA = 45;

const STATUS_KEYS = [
    "warning",
    "success",
    "error",
    "info"
];

const FILLED_STATUS_LABELS = [
    {
        foreground: "content.text.onPrimary",
        statusKey: "success"
    },
    {
        foreground: "content.text.onPrimary",
        statusKey: "danger"
    },
    {
        foreground: "system.on.warning",
        statusKey: "warning"
    },
    {
        foreground: "content.text.onPrimary",
        statusKey: "info"
    }
];

const CONTRAST_PAIRS: Array<ContrastPair> = [
    {
        labelKey: "create.report.textHighGround",
        background: "content.container.default",
        foreground: "content.text.high",
        id: "text-high-ground",
        apcaTarget: BODY_APCA,
        wcagTarget: TEXT_WCAG
    },
    {
        labelKey: "create.report.textHighSubtle",
        background: "content.container.subtle",
        foreground: "content.text.high",
        id: "text-high-subtle",
        apcaTarget: BODY_APCA,
        wcagTarget: TEXT_WCAG
    },
    {
        background: "content.container.default",
        labelKey: "create.report.textMid",
        foreground: "content.text.mid",
        isApcaInfoInDark: true,
        apcaTarget: TEXT_APCA,
        wcagTarget: TEXT_WCAG,
        id: "text-mid"
    },
    {
        background: "content.container.default",
        labelKey: "create.report.textLow",
        foreground: "content.text.low",
        isApcaInfoInDark: true,
        apcaTarget: TEXT_APCA,
        wcagTarget: TEXT_WCAG,
        id: "text-low"
    },
    {
        background: "content.container.default",
        foreground: "content.text.link",
        labelKey: "create.report.link",
        isApcaInfoInDark: true,
        apcaTarget: TEXT_APCA,
        wcagTarget: TEXT_WCAG,
        id: "link"
    },
    {
        background: "content.container.primary",
        foreground: "content.text.onPrimary",
        labelKey: "create.report.onPrimary",
        apcaTarget: TEXT_APCA,
        wcagTarget: TEXT_WCAG,
        id: "on-primary"
    },
    ...FILLED_STATUS_LABELS.map(({
        foreground,
        statusKey
    }): ContrastPair => ({
        labelKey: `create.report.filled.${statusKey}`,
        background: `content.container.${statusKey}`,
        id: `filled-${statusKey}`,
        apcaTarget: TEXT_APCA,
        wcagTarget: TEXT_WCAG,
        foreground
    })),
    {
        background: "content.container.default",
        foreground: "content.container.primary",
        labelKey: "create.report.primaryFill",
        wcagTarget: MARK_WCAG,
        id: "primary-fill",
        apcaTarget: 0
    },
    {
        foreground: "system.state.overlay.focus.default",
        background: "content.container.default",
        labelKey: "create.report.focusRing",
        wcagTarget: MARK_WCAG,
        id: "focus-ring",
        apcaTarget: 0
    },
    {
        background: "content.container.default",
        labelKey: "create.report.iconLow",
        foreground: "content.icon.low",
        apcaTarget: ICON_APCA,
        wcagTarget: MARK_WCAG,
        id: "icon-low"
    },
    {
        background: "content.container.default",
        labelKey: "create.report.borderDefault",
        foreground: "content.border.default",
        id: "border-default",
        wcagTarget: 0,
        apcaTarget: 0
    },
    ...STATUS_KEYS.map((statusKey): ContrastPair => ({
        foreground: `system.onContainer.${statusKey}`,
        labelKey: `create.report.status.${statusKey}`,
        background: `system.low.${statusKey}`,
        id: `status-${statusKey}`,
        isApcaInfoInDark: true,
        apcaTarget: TEXT_APCA,
        wcagTarget: TEXT_WCAG
    }))
];

const readToken = (tokens: CreateThemeTokens, path: string) => {
    let current: TokenTree | string | undefined = tokens as TokenTree;

    for(const key of path.split(".")) {
        if(current === undefined || typeof current === "string") {
            return "";
        }

        current = current[key];
    }

    return typeof current === "string" ? current : "";
};

const resolveColor = (value: string, background: ColorVector): ColorVector => {
    const parsed = parseColor(value);

    if(!parsed) {
        return background;
    }

    if(!parsed.isRgba) {
        return parsed.rgb;
    }

    return compositeOver({
        foreground: parsed.rgb,
        alpha: parsed.alpha,
        background
    });
};

const roundTo = (value: number, digits: number) => {
    const factor = 10 ** digits;

    return Math.round(value * factor) / factor;
};

const checkPair = ({
    themeKey,
    tokens,
    pair
}: {
    tokens: CreateThemeTokens;
    themeKey: CreateThemeKey;
    pair: ContrastPair;
}): CreateContrastCheck => {
    const ground = resolveColor(readToken(tokens, GROUND_PATH), [
        1,
        1,
        1
    ]);

    const background = resolveColor(readToken(tokens, pair.background), ground);

    const foreground = resolveColor(readToken(tokens, pair.foreground), background);

    const wcag = getContrastRatio(foreground, background);

    const apca = pair.apcaTarget > 0 ? Math.abs(getApcaContrast({
        text: foreground,
        background
    })) : 0;

    const isApcaInfo = themeKey === "dark" && pair.isApcaInfoInDark === true;

    return {
        isPassing: wcag >= pair.wcagTarget && (isApcaInfo || apca >= pair.apcaTarget),
        isGated: pair.wcagTarget > 0 || pair.apcaTarget > 0,
        id: `${pair.id}-${themeKey}`,
        apcaTarget: pair.apcaTarget,
        wcagTarget: pair.wcagTarget,
        bg: formatHex(background),
        fg: formatHex(foreground),
        labelKey: pair.labelKey,
        apca: roundTo(apca, 1),
        wcag: roundTo(wcag, 2),
        isApcaInfo,
        themeKey
    };
};

const getExtremeIndex = (values: Array<number>, isLowest: boolean) => {
    return values.reduce((best, value, index) => {
        const bestValue = values[best] ?? value;

        return (isLowest ? value < bestValue : value > bestValue) ? index : best;
    }, 0);
};

const getWorstPair = (pairs: Array<ChartPair>, getValue: (delta: ChartPairDelta) => number) => {
    return pairs.reduce<ChartPair | null>((worst, pair) => !worst || getValue(pair.delta) < getValue(worst.delta) ? pair : worst, null);
};

const buildChartCheck = ({
    isPassing,
    themeKey,
    isGated,
    chart,
    fg,
    bg
}: {
    themeKey: CreateThemeKey;
    chart: CreateChartCheck;
    isPassing: boolean;
    isGated: boolean;
    fg: ColorVector;
    bg: ColorVector;
}): CreateContrastCheck => {
    return {
        labelKey: `create.report.chart.${chart.key}`,
        id: `chart-${chart.key}-${themeKey}`,
        bg: formatHex(bg),
        fg: formatHex(fg),
        isApcaInfo: false,
        apcaTarget: 0,
        wcagTarget: 0,
        isPassing,
        themeKey,
        apca: 0,
        isGated,
        wcag: 0,
        chart
    };
};

const checkChartSeries = ({
    themeKey,
    tokens
}: {
    tokens: CreateThemeTokens;
    themeKey: CreateThemeKey;
}): Array<CreateContrastCheck> => {
    const ground = resolveColor(readToken(tokens, GROUND_PATH), [
        1,
        1,
        1
    ]);

    const colors = Array.from({
        length: CHART_SERIES_COUNT
    }, (_, index) => resolveColor(readToken(tokens, `${CHART_SERIES_PATH}.${index + 1}`), ground));

    const getColor = (index: number) => colors[index] ?? ground;

    const lightness = colors.map((color) => rgbToOklch(color)[0]);

    const chroma = colors.map((color) => rgbToOklch(color)[1]);

    const contrast = colors.map((color) => getContrastRatio(color, ground));

    const band = CHART_LIGHTNESS_BANDS[themeKey];

    const darkest = getExtremeIndex(lightness, true);

    const lightest = getExtremeIndex(lightness, false);

    const leastChroma = getExtremeIndex(chroma, true);

    const leastContrast = getExtremeIndex(contrast, true);

    const allPairs = colors.flatMap((first, firstIndex) => colors.slice(firstIndex + 1).map((second, offset): ChartPair => ({
        delta: getChartPairDelta(first, second),
        second: firstIndex + offset + 1,
        first: firstIndex
    })));

    const adjacentPairs = allPairs.filter((pair) => pair.second === pair.first + 1);

    const worstCvd = getWorstPair(adjacentPairs, (delta) => delta.cvd);

    const worstTritan = getWorstPair(adjacentPairs, (delta) => delta.tritan);

    const worstNormal = getWorstPair(adjacentPairs, (delta) => delta.normal);

    const worstSpread = getWorstPair(allPairs, (delta) => delta.normal);

    const getPairCheck = ({
        extraValues = [],
        worstKind,
        getValue,
        target,
        pair,
        key
    }: {
        getValue: (delta: ChartPairDelta) => number;
        worstKind?: ChartPairDelta["worstKind"];
        extraValues?: Array<number>;
        key: CreateChartCheckKey;
        pair: ChartPair | null;
        target: number;
    }) => {
        const value = pair ? getValue(pair.delta) : 0;

        return buildChartCheck({
            bg: pair ? getColor(pair.second) : ground,
            fg: pair ? getColor(pair.first) : ground,
            chart: {
                targets: target > 0 ? [
                    target
                ] : [],
                series: pair ? [
                    pair.first + 1,
                    pair.second + 1
                ] : [],
                values: [
                    value,
                    ...extraValues
                ],
                worstKind,
                key
            },
            isPassing: value >= target,
            isGated: target > 0,
            themeKey
        });
    };

    return [
        buildChartCheck({
            isPassing: lightness.every((value) => value >= band[0] && value <= band[1]),
            chart: {
                values: [
                    lightness[darkest] ?? 0,
                    lightness[lightest] ?? 0
                ],
                key: "lightness",
                series: [
                    darkest + 1,
                    lightest + 1
                ],
                targets: band
            },
            bg: getColor(lightest),
            fg: getColor(darkest),
            isGated: true,
            themeKey
        }),
        buildChartCheck({
            isPassing: (chroma[leastChroma] ?? 0) >= CHART_CHROMA_FLOOR,
            chart: {
                values: [
                    chroma[leastChroma] ?? 0
                ],
                targets: [
                    CHART_CHROMA_FLOOR
                ],
                series: [
                    leastChroma + 1
                ],
                key: "chroma"
            },
            fg: getColor(leastChroma),
            isGated: true,
            bg: ground,
            themeKey
        }),
        getPairCheck({
            extraValues: [
                worstTritan ? worstTritan.delta.tritan : 0
            ],
            worstKind: worstCvd?.delta.worstKind,
            getValue: (delta) => delta.cvd,
            target: CHART_CVD_TARGET,
            pair: worstCvd,
            key: "cvd"
        }),
        getPairCheck({
            getValue: (delta) => delta.normal,
            target: CHART_NORMAL_FLOOR,
            pair: worstNormal,
            key: "normal"
        }),
        buildChartCheck({
            isPassing: (contrast[leastContrast] ?? 0) >= CHART_CONTRAST_MIN,
            chart: {
                values: [
                    contrast[leastContrast] ?? 0
                ],
                targets: [
                    CHART_CONTRAST_MIN
                ],
                series: [
                    leastContrast + 1
                ],
                key: "contrast"
            },
            fg: getColor(leastContrast),
            isGated: true,
            bg: ground,
            themeKey
        }),
        getPairCheck({
            getValue: (delta) => delta.normal,
            pair: worstSpread,
            key: "spread",
            target: 0
        })
    ];
};

export const getContrastReport = (palette: CreatePalette): Array<CreateContrastCheck> => {
    const themeKeys: Array<CreateThemeKey> = [
        "light",
        "dark"
    ];

    return themeKeys.flatMap((themeKey) => {
        const tokens = palette.themes[themeKey];

        return [
            ...CONTRAST_PAIRS.map((pair) => checkPair({
                themeKey,
                tokens,
                pair
            })),
            ...checkChartSeries({
                themeKey,
                tokens
            })
        ];
    });
};
